import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { MapPin, Ticket, Image as ImageIcon, Calendar, X } from "lucide-react";

const VIDEO_URL_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/arqgsj0u_Sequence%2001_10.mp4";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Events() {
  const kicker = useContent("page.events.kicker", "/ 05 — EVENTS");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const title = useContent("page.events.title", "ARCHIVE 2021 — 2026");
  const upcomingLabel = useContent("page.events.upcoming_label", "Upcoming");
  const pastLabel = useContent("page.events.past_label", "Past Rituals");
  const heroVideoUrl = useContent("events.hero.video_url", VIDEO_URL_DEFAULT);
  const heroKicker = useContent("events.hero.kicker", "/ LIVE FOOTAGE");
  const heroTitle = useContent("events.hero.title", "RITUAL IN MOTION");

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = useMemo(
    () =>
      events
        .filter((e) => e.is_upcoming || e.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date)),
    [events, today]
  );
  const past = useMemo(
    () =>
      events
        .filter((e) => !(e.is_upcoming || e.date >= today))
        .sort((a, b) => b.date.localeCompare(a.date)),
    [events, today]
  );
  const byYear = useMemo(() => {
    const map = {};
    past.forEach((e) => {
      (map[e.year] = map[e.year] || []).push(e);
    });
    return map;
  }, [past]);
  const years = useMemo(() => Object.keys(byYear).sort((a, b) => b - a), [byYear]);

  return (
    <div data-testid="events-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        {/* VIDEO HERO */}
        <div
          className="relative w-full aspect-[21/9] mb-14 hairline overflow-hidden bg-black"
          data-testid="events-video-hero"
        >
          <video
            src={heroVideoUrl}
            key={heroVideoUrl}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ filter: "contrast(1.05)" }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.7) 100%)",
            }}
          />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-px bg-silver-200" />
                <span className="font-title text-[0.6rem] tracking-[0.4em] text-silver-200">
                  {heroKicker}
                </span>
              </div>
              <div className="font-title text-xl md:text-2xl tracking-[0.12em] text-silver-100">
                {heroTitle}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12" data-testid="events-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>

          <div className="flex gap-2 mt-10" data-testid="events-filters">
            {[
              { k: "all", label: "All" },
              { k: "upcoming", label: "Upcoming" },
              { k: "past", label: "Past" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`font-title text-[0.65rem] tracking-[0.26em] uppercase px-4 py-2 border transition-all ${
                  filter === f.k
                    ? "border-silver-200 text-silver-100 bg-silver-900/60"
                    : "border-silver-800 text-silver-500 hover:text-silver-200 hover:border-silver-500"
                }`}
                data-testid={`filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <p className="font-body text-silver-500 text-sm" data-testid="events-loading">
            Loading archive…
          </p>
        )}
        {err && (
          <p className="font-body text-red-400 text-sm" data-testid="events-error">
            {err}
          </p>
        )}

        {!loading && (filter === "all" || filter === "upcoming") && (
          <section className="mb-20" data-testid="upcoming-section">
            <div className="hairline-t pt-8 mb-8 flex items-baseline justify-between">
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.12em] text-silver-100">
                {upcomingLabel}
              </h2>
              <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                / {upcoming.length} {upcoming.length === 1 ? "SHOW" : "SHOWS"}
              </span>
            </div>
            {upcoming.length === 0 ? (
              <p className="font-body text-silver-500 text-sm py-4" data-testid="upcoming-empty">
                No upcoming events scheduled yet — check back soon.
              </p>
            ) : (
              <div className="flex flex-col" data-testid="upcoming-list">
                {upcoming.map((ev) => (
                  <EventRow key={ev.id} ev={ev} upcoming />
                ))}
              </div>
            )}
          </section>
        )}

        {!loading && (filter === "all" || filter === "past") && (
          <section data-testid="past-section">
            <div className="hairline-t pt-8 mb-8 flex items-baseline justify-between">
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.12em] text-silver-100">
                {pastLabel}
              </h2>
              <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                / {past.length} SHOWS
              </span>
            </div>
            {years.map((y) => (
              <div key={y} className="mb-14" data-testid={`past-year-${y}`}>
                <div className="flex items-baseline gap-6 mb-4">
                  <h3 className="font-title text-5xl md:text-6xl text-silver-glow">{y}</h3>
                  <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                    / {byYear[y].length} SHOWS
                  </span>
                </div>
                <div className="flex flex-col">
                  {byYear[y].map((ev) => (
                    <EventRow key={ev.id} ev={ev} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {!loading && events.length === 0 && (
          <p className="font-body text-silver-500 text-sm" data-testid="events-empty">
            No events yet.
          </p>
        )}
      </div>
    </div>
  );
}

function EventRow({ ev, upcoming = false }) {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <article
        className="event-card grid md:grid-cols-12 gap-4 items-center"
        data-testid={`event-row-${ev.id}`}
      >
        {/* Poster image — small, click to zoom */}
        <div className="md:col-span-1 flex items-center">
          {ev.poster_url ? (
            <button
              type="button"
              onClick={() => setZoom(true)}
              className="block w-16 h-16 md:w-20 md:h-20 hairline overflow-hidden hover:border-silver-200 transition-all cursor-zoom-in"
              data-testid={`event-poster-thumb-${ev.id}`}
              title="Click to enlarge"
            >
              <img
                src={ev.poster_url}
                alt={ev.title}
                className="w-full h-full object-cover hover:scale-110 transition-transform"
              />
            </button>
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 hairline flex items-center justify-center text-silver-700">
              <Calendar size={20} />
            </div>
          )}
        </div>

        <div className="md:col-span-2 flex items-center gap-3">
          <Calendar size={14} className="text-silver-500 shrink-0" />
          <div>
            <div className="font-title text-sm tracking-[0.15em] text-silver-100">
              {formatDate(ev.date)}
            </div>
          </div>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-title text-base md:text-lg tracking-[0.1em] text-silver-200">
            {ev.title}
          </h4>
          {ev.notes && <p className="font-body text-xs text-silver-500 mt-1">{ev.notes}</p>}
        </div>

        <div className="md:col-span-3 flex items-center gap-2 text-silver-400">
          <MapPin size={12} className="shrink-0" />
          <span className="font-body text-xs tracking-wide">
            {[ev.venue, ev.city, ev.country].filter(Boolean).join(" · ")}
          </span>
        </div>

        <div className="md:col-span-2 flex items-center gap-3 md:justify-end">
          {upcoming && ev.ticket_url && (
            <a
              href={ev.ticket_url}
              target="_blank"
              rel="noreferrer"
              className="font-title text-[0.6rem] tracking-[0.26em] uppercase text-silver-100 border border-silver-200 px-3 py-1.5 hover:bg-silver-200 hover:text-black transition-colors flex items-center gap-1"
              data-testid={`event-tickets-${ev.id}`}
            >
              <Ticket size={12} /> Tickets
            </a>
          )}
          {!upcoming && ev.gallery_url && (
            <a
              href={ev.gallery_url}
              target="_blank"
              rel="noreferrer"
              className="event-media-btn font-title text-[0.6rem] tracking-[0.26em] uppercase text-silver-100 border border-silver-200 px-3 py-1.5 flex items-center gap-1.5"
              data-testid={`event-gallery-${ev.id}`}
              title="View event gallery / media"
            >
              <span className="event-media-dot" /> Media
            </a>
          )}
        {ev.poster_url && (
          <button
            type="button"
            onClick={() => setZoom(true)}
            className="font-title text-[0.6rem] tracking-[0.26em] uppercase text-silver-400 hover:text-silver-100 transition-colors"
            data-testid={`event-poster-${ev.id}`}
          >
            Poster →
          </button>
        )}
        </div>
      </article>

      {/* Zoom modal — almost full screen, click anywhere to close */}
      {zoom && ev.poster_url && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 backdrop-blur-sm cursor-zoom-out px-4 py-6"
          onClick={() => setZoom(false)}
          data-testid={`event-poster-modal-${ev.id}`}
        >
          <img
            src={ev.poster_url}
            alt={ev.title}
            className="max-w-[95vw] max-h-[92vh] object-contain hairline"
          />
          <button
            onClick={() => setZoom(false)}
            className="absolute top-6 right-6 text-silver-200 hover:text-silver-100"
            aria-label="Close"
          >
            <X size={28} />
          </button>
        </div>
      )}
    </>
  );
}
